import { useState, useMemo } from 'react'
import { Head, router } from '@inertiajs/react'
import AppShell, { type AppShellProps } from '@/components/AppShell'
import { Button } from '@/components/ui/button'
import { ArrowLeft } from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface FieldDef {
  key: string
  label: string
  categories: string[]
}

interface ReportType {
  value: string
  label: string
}

interface Props extends Omit<AppShellProps, 'children'> {
  event: { id: number; name: string }
  availableFields: FieldDef[]
  reportTypes: ReportType[]
  urls: AppShellProps['urls'] & {
    create: string
    dashboard: string
  }
  translations: AppShellProps['translations'] & {
    createReport: string
    reportType: string
    submit: string
    backToDashboard: string
  }
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function Prepare(props: Props) {
  const { event, availableFields, reportTypes, urls, translations: t, ...shell } = props

  const [reportType, setReportType] = useState(reportTypes[0]?.value || '')
  const [selected, setSelected] = useState<Record<string, boolean>>({})

  // which category drives visibility: "statistics" → stats, else → general
  const isStats = reportType === reportTypes[0]?.value

  const visibleFields = useMemo(() => {
    return availableFields.filter((f) => {
      if (isStats) return f.categories.includes('stats')
      return f.categories.includes('general') || f.categories.includes('stats')
    })
  }, [availableFields, isStats])

  function toggle(key: string) {
    setSelected((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const data: Record<string, string> = { 'report[report_type]': reportType }
    Object.entries(selected).forEach(([k, v]) => {
      if (v) data[`report[${k}]`] = '1'
    })
    router.post(urls.create, data)
  }

  return (
    <AppShell {...shell} urls={urls} translations={t}>
      <Head title={`${t.createReport} — ${event.name}`} />

      <div className="mx-auto max-w-2xl py-8 px-4">
        <Button
          variant="ghost"
          size="sm"
          className="mb-6 text-[#91918E]"
          onClick={() => router.visit(urls.dashboard)}
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          {t.backToDashboard}
        </Button>

        <h1 className="text-xl font-semibold text-[#37352F] mb-6">{t.createReport}</h1>
        <p className="text-sm text-[#91918E] mb-6">{event.name}</p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Report Type */}
          <div>
            <label className="block text-sm font-medium text-[#37352F] mb-2">
              {t.reportType}
            </label>
            <select
              value={reportType}
              onChange={(e) => {
                setReportType(e.target.value)
                setSelected({})
              }}
              className="w-full rounded-lg border border-[#E8E8E5] bg-white px-3 py-2 text-sm text-[#37352F] focus:border-[#2383E2] focus:outline-none focus:ring-1 focus:ring-[#2383E2]"
            >
              {reportTypes.map((rt) => (
                <option key={rt.value} value={rt.value}>
                  {rt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Field checkboxes */}
          <div className="grid grid-cols-2 gap-4">
            {visibleFields.map((field) => (
              <label
                key={field.key}
                className="flex items-center gap-3 rounded-lg border border-[#E8E8E5] p-3 cursor-pointer transition-colors hover:bg-[#F1F1EF]"
              >
                <input
                  type="checkbox"
                  checked={!!selected[field.key]}
                  onChange={() => toggle(field.key)}
                  className="h-4 w-4 rounded border-[#E8E8E5] text-[#2383E2] focus:ring-[#2383E2]"
                />
                <span className="text-sm text-[#37352F]">{field.label}</span>
              </label>
            ))}
          </div>

          <Button type="submit" className="w-full bg-[#2383E2] hover:bg-[#1b6ec2]">
            {t.submit}
          </Button>
        </form>
      </div>
    </AppShell>
  )
}
